/**
 * Formatting rules for the console.
 *
 * Two owner directives are hard-coded here and must not be overridden per-screen:
 *   1. Every date and time renders in moment's `lll` format, locale en-in
 *      (specs/features/agent-console.yaml, owner directive 2026-07-29).
 *      moment's `lll` under en-in is `D MMM YYYY, h:mm a` -> "30 Jul 2026, 5:38 pm".
 *      We reproduce that with Intl rather than shipping moment.
 *   2. Money is INR with Indian lakh/crore grouping -> "10,20,566.00".
 *
 * Every helper is pure and total: it never throws on null/undefined/NaN, it
 * returns the em-dash placeholder. A missing number must look missing, never
 * like a zero — a zero balance and an unavailable balance mean different things
 * on a money screen.
 */

export const EMPTY = '—' // em dash

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

/* ------------------------------------------------------------------ money -- */

const inrGroup = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const inrGroup0 = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

export interface MoneyOpts {
  /** Show the ₹ symbol. Default true. Turn off inside a column already headed "₹". */
  symbol?: boolean
  /** Force a leading + on positives. Use on P&L, never on balances. */
  signed?: boolean
  /** Drop the paise. Use on axis ticks and dense tables, never on a ledger row. */
  round?: boolean
}

/** `1020566.5` -> `"₹10,20,566.50"`. Null-safe. */
export function money(value: number | null | undefined, opts: MoneyOpts = {}): string {
  if (!isNum(value)) return EMPTY
  const { symbol = true, signed = false, round = false } = opts
  const fmt = round ? inrGroup0 : inrGroup
  const abs = fmt.format(Math.abs(value))
  const sign = value < 0 ? '-' : signed && value > 0 ? '+' : ''
  return `${sign}${symbol ? '₹' : ''}${abs}`
}

/**
 * Compact Indian money for KPI tiles and axis labels.
 * `1020566` -> `"₹10.2L"`, `13500000` -> `"₹1.35Cr"`, `4820` -> `"₹4,820"`.
 * Three significant figures, always (see `trim`) — the example here used to say
 * `₹10.21L`, which no build has ever produced.
 * Never used where the exact figure matters — tiles pair this with a title
 * attribute carrying the full value.
 */
export function moneyCompact(value: number | null | undefined, symbol = true): string {
  if (!isNum(value)) return EMPTY
  const s = symbol ? '₹' : ''
  const sign = value < 0 ? '-' : ''
  const n = Math.abs(value)
  if (n >= 1e7) return `${sign}${s}${trim(n / 1e7)}Cr`
  if (n >= 1e5) return `${sign}${s}${trim(n / 1e5)}L`
  if (n >= 1e3) return `${sign}${s}${inrGroup0.format(n)}`
  return `${sign}${s}${trim(n)}`
}

/**
 * Drop a purely-zero FRACTION — `2.50` -> `2.5`, `2.00` -> `2`.
 *
 * The `includes('.')` guard is the whole point. The regex alone makes the
 * decimal point optional, so it happily ate the trailing zeros of an INTEGER
 * string: `trim(870)` returned `"87"` and `trim(200)` returned `"2"`, which put
 * ₹870 on screen as ₹87 everywhere `moneyCompact` renders a rupee figure between
 * ₹100 and ₹999.99 (and every crore value at or above ₹100 Cr). Anchoring the
 * strip to a string that actually has a fraction is the fix; widening the regex
 * to `/\.\d*?0+$/` would work too but reads as though the dot were incidental.
 */
const trim = (n: number) => {
  const r = n >= 100 ? n.toFixed(0) : n >= 10 ? n.toFixed(1) : n.toFixed(2)
  return r.includes('.') ? r.replace(/\.?0+$/, '') : r
}

/* ---------------------------------------------------------------- numbers -- */

/** Plain integer with Indian grouping. `15708` -> `"15,708"`. */
export function count(value: number | null | undefined): string {
  return isNum(value) ? inrGroup0.format(value) : EMPTY
}

/** `0.0734` -> `"7.34%"`. Pass a ratio, not an already-multiplied number. */
export function percent(ratio: number | null | undefined, digits = 1): string {
  if (!isNum(ratio)) return EMPTY
  return `${(ratio * 100).toFixed(digits)}%`
}

/** `7.34` -> `"7.34%"`. For values already stored as percentage points. */
export function pct(points: number | null | undefined, digits = 2): string {
  if (!isNum(points)) return EMPTY
  return `${points.toFixed(digits)}%`
}

/** Decimal odds always carry two places — 1.98 and 1.9 must not look different. */
export function odds(value: number | null | undefined): string {
  return isNum(value) ? value.toFixed(2) : EMPTY
}

/* ------------------------------------------------------------------ dates -- */

const LLL = new Intl.DateTimeFormat('en-IN', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
})

const LL = new Intl.DateTimeFormat('en-IN', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

const HM = new Intl.DateTimeFormat('en-IN', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
})

/** Intl emits U+202F / U+00A0 around the am/pm marker; normalise so copy-paste and
    string assertions in tests behave. */
const NBSP = /[\u202f\u00a0]/g

/** A bare `YYYY-MM-DD`, which is the shape every range param and day-grain key uses. */
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

const asDate = (v: Date | string | number | null | undefined): Date | null => {
  if (v == null) return null
  // `new Date('2026-07-20')` is spec'd as UTC midnight, but `new Date('2026-07-20T00:00:00')`
  // is LOCAL midnight — so an ISO *day* formatted through `ll` rendered one day
  // early in every timezone behind UTC. A 30-day window ending 31 Jul labelled
  // itself "1 Jul – 30 Jul" in a US browser. Only labels were ever wrong (the
  // data layer compares these as strings), but a range that misstates its own
  // dates is exactly the defect this console exists to remove.
  const d =
    v instanceof Date ? v : typeof v === 'string' && ISO_DAY.test(v) ? new Date(`${v}T00:00:00`) : new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * THE date format for this console — moment `lll`, locale en-in.
 * "30 Jul 2026, 5:38 pm". Use this everywhere a timestamp is shown.
 */
export function lll(value: Date | string | number | null | undefined): string {
  const d = asDate(value)
  return d ? LLL.format(d).replace(NBSP, ' ') : EMPTY
}

/** Date only — "30 Jul 2026". For day-grain rows and axis labels. */
export function ll(value: Date | string | number | null | undefined): string {
  const d = asDate(value)
  return d ? LL.format(d).replace(NBSP, ' ') : EMPTY
}

/** Time only — "5:38 pm". Only ever inside a group already labelled with its date. */
export function hm(value: Date | string | number | null | undefined): string {
  const d = asDate(value)
  return d ? HM.format(d).replace(NBSP, ' ') : EMPTY
}

/** "just now" / "12m ago" / "3h ago" / "6d ago", then falls back to `ll`. */
export function ago(value: Date | string | number | null | undefined, now = Date.now()): string {
  const d = asDate(value)
  if (!d) return EMPTY
  const secs = Math.round((now - d.getTime()) / 1000)
  if (secs < 45) return 'just now'
  const mins = Math.round(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.round(hrs / 24)
  if (days <= 13) return `${days}d ago`
  return ll(d)
}

/** ISO `YYYY-MM-DD` in local time — the key format for every day-grain series. */
export function isoDay(value: Date | string | number): string {
  const d = asDate(value)
  if (!d) return ''
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/** "30 Jul" — compact axis label for daily series. */
export function dayLabel(value: Date | string | number): string {
  const d = asDate(value)
  if (!d) return EMPTY
  return new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short' })
    .format(d)
    .replace(NBSP, ' ')
}

/* --------------------------------------------------------------- semantics -- */

export type Tone = 'profit' | 'loss' | 'neutral'

/**
 * The single place that decides whether a number is green or red.
 *
 * `zeroIsNeutral` matters: on a P&L column exactly 0 is neutral grey, but on an
 * exposure column 0 means "no liability" which we still want to read as calm.
 */
export function toneOf(value: number | null | undefined, invert = false): Tone {
  if (!isNum(value) || value === 0) return 'neutral'
  const positive = value > 0
  return (invert ? !positive : positive) ? 'profit' : 'loss'
}

/** Initials for an avatar — "zinrtest1" -> "ZI", "Danny Boy" -> "DB". */
export function initials(name: string): string {
  const parts = name.trim().split(/[\s._-]+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase()
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase()
}
